import { useId, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from '../../../icons';
import { Checkbox } from '../Checkbox';
import { Skeleton } from '../Skeleton';
import './Table.css';

/**
 * Table — 손으로 짠 `<table>` **130회 / 64파일** 을 흡수한다 (디자인 정본 E1).
 *
 * ★★★ **지금은 표를 «이름으로 열거»해서 테마를 입힌다** — `theme-surfaces.css` 가
 *    `.settings-table` `.workspace-list-table` … 다섯 개를 적어 두고 잡는다.
 *    **거기 없는 표는 회사 테마 색을 안 따라간다.** `.wc-table` 한 이름으로 수렴시키면
 *    그 열거가 필요 없어진다.
 *
 * ★★ **밀도는 props 로 내리지 않는다.** 컨테이너에 `data-density="compact|dense"` 를 걸면
 *    안쪽 표가 상속받는다(정본 E7). 컴포넌트마다 prop 을 받으면 **한 곳을 빠뜨렸을 때
 *    그 표만 다른 높이**가 된다.
 *
 * ★ **zebra(줄무늬)를 쓰지 않는다** — hover 와 구분이 안 된다.
 * ★ **세로선을 긋지 않는다** — 행 하단 선 하나로 충분하다.
 * ★ 넘칠 때는 **표를 감싼 상자 안에서만** 가로로 흐른다. 페이지 본문은 안 흐른다.
 */

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  /** 없으면 `row[key]` 를 그대로 그린다. */
  render?: (row: T, index: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
  /** 머리를 눌러 정렬할 수 있는 열. */
  sortable?: boolean;
  /** 숫자 칸 — 오른쪽 정렬 + 등폭 숫자(자리가 흔들리면 안 된다). */
  numeric?: boolean;
  /** 가로로 흘러도 왼쪽에 붙어 있는 열. */
  sticky?: boolean;
  /**
   * 이 칸이 **옆 칸까지 먹는** 행이 있을 때 (2026-09-04 · 채택 3단계 ③ 근태).
   *
   * ★★ **왜 필요한가.** 값이 빈 이유를 «칸 하나»로는 못 적는 표가 있다 —
   *    근태 현황의 「종일 휴가 · 종류 비공개」 · 「출장으로 간주 · 기준 09:00」이 그렇다.
   *    출근·퇴근·근무 세 칸이 그냥 비면 **왜 비었는지 알 수 없다.**
   * ★ 1(기본)이면 아무 일도 없다. 2 이상이면 **뒤따르는 (n-1) 개 열은 그 행에서 안 그린다**.
   * ★ 머리(`<th>`)는 그대로다 — 합치는 것은 **몸통의 한 행**뿐이다.
   */
  colSpan?: (row: T, index: number) => number | undefined;
  /**
   * 이 열이 **그 행의 이름**일 때 (2026-09-04). `<th scope="row">` 로 그린다.
   *
   * ★★ 손으로 짠 표들은 사람 이름 칸을 `<th scope="row">` 로 두고 있었다 —
   *    읽어 주는 프로그램이 「출근 09:02」 를 읽을 때 **「최민석의」** 를 앞에 붙여 준다.
   *    전부 `<td>` 로 갈아타면 그 이름표가 **말없이 사라진다.**
   * ★ 한 표에 하나만 준다(둘이면 둘 다 이름표가 되어 뜻이 흐려진다).
   */
  rowHeader?: boolean;
}

export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export interface TableProps<T> {
  /** 이 표가 무엇인지. 화면에는 안 보이고 읽어 주기만 한다 — **생략하지 않는다.** */
  caption: string;
  columns: ReadonlyArray<TableColumn<T>>;
  rows: readonly T[];
  rowKey: (row: T, index: number) => string;

  sort?: SortState;
  onSortChange?: (next: SortState) => void;

  /** 선택 열을 켠다. 주면 첫 열에 체크박스가 선다. */
  selectedKeys?: readonly string[];
  onSelectionChange?: (keys: string[]) => void;
  /**
   * 고르기 칸의 읽어 주는 이름 (2026-09-02 · 채택 3단계).
   * 안 주면 「3번째 행 선택」이 된다 — 자리는 알겠는데 **무엇을 고르는지는 모른다.**
   * 행에 이름이 있으면(사람·사업장·게시판) 그것을 주는 쪽이 옳다: 「홍길동 고르기」.
   */
  rowSelectionLabel?: (row: T, index: number) => string;
  /**
   * 이 줄을 **고를 수 있나** (2026-09-06 · 채택 3단계 ③ 근태).
   *
   * ★★ **왜 필요했나** — 연차촉진은 「지금 보낼 수 있는 사람」만 고르게 하려고 줄마다
   *    체크박스를 **잠가** 두고 있었다. 정본 선택 열로 갈아타면 그 잠금이 **말없이 사라져**,
   *    보낼 수 없는 사람을 골라 놓고 단추를 눌러도 아무 일이 안 일어난다.
   * ★ 「전체 선택」도 이것을 따른다 — 못 고르는 줄까지 담으면 머리 체크박스가 영영 «반쯤»
   *   에서 멈춘다.
   * ★ 안 주면 **전부 고를 수 있다**(지금까지와 같다).
   */
  rowSelectable?: (row: T, index: number) => boolean;

  /** 머리는 남기고 본문만 뼈대로 그린다. */
  loading?: boolean;
  /** 행이 없을 때 본문 자리에 그릴 것. **표 껍데기는 남긴다.** */
  empty?: ReactNode;

  /**
   * 행 상태를 겉으로 나타내는 클래스 (2026-09-02 · 채택 3단계 ③).
   *
   * ★★ **왜 필요했나** — 손으로 짠 표들은 「기한이 지난 줄 · 미이수 줄 · 확정 전 줄」에
   *    `is-overdue` 같은 클래스를 붙여 **왼쪽에 경고 줄무늬**를 그리고 있었다(인사 화면 여덟 곳).
   *    이 컴포넌트로 갈아타면 그 표시가 **말없이 사라진다** — 행 자체에 아무것도 붙일 수 없어서다.
   * ★ 색만으로 뜻을 만들지 말 것(정본 P5) — 줄무늬는 **글자로도 적힌 것**을 거들 때만 쓴다.
   * ★ 선택 상태(`is-selected`)는 이 컴포넌트가 스스로 붙인다 — 여기서 또 주지 않는다.
   */
  rowClassName?: (row: T, index: number) => string | undefined;

  onRowClick?: (row: T, index: number) => void;
  /** 마지막 열 — 행에 손이 닿거나 **키보드 포커스가 들어올 때** 보인다. */
  rowActions?: (row: T, index: number) => ReactNode;
  /** 합계 줄. `<tfoot>` 에 들어간다. */
  footer?: ReactNode;
  stickyHeader?: boolean;
  className?: string;
  /** 표를 감싼 상자에 붙는 클래스(가로 스크롤을 맡는 자리). */
  wrapClassName?: string;
}

const SKELETON_ROWS = 5;

export function Table<T>({
  caption,
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  selectedKeys,
  onSelectionChange,
  rowSelectionLabel,
  rowSelectable,
  loading = false,
  empty,
  rowClassName,
  onRowClick,
  rowActions,
  footer,
  stickyHeader = false,
  className,
  wrapClassName,
}: TableProps<T>) {
  const uid = useId();
  const selectable = selectedKeys != null && onSelectionChange != null;
  const selected = new Set(selectedKeys ?? []);
  /** 「전체 선택」이 담을 것 — **고를 수 있는 줄만**이다(못 고르는 줄을 담으면 영영 반쯤이 된다). */
  const selectableKeys = rows.flatMap((r, i) => ((rowSelectable?.(r, i) ?? true) ? [rowKey(r, i)] : []));
  const checkedCount = selectableKeys.filter((k) => selected.has(k)).length;
  const allChecked = selectableKeys.length > 0 && checkedCount === selectableKeys.length;
  /** ★ 「하나도 안 골랐다」와 「몇 개만 골랐다」를 같게 보이지 않게 하는 값. */
  const someChecked = checkedCount > 0 && !allChecked;

  const colCount = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  const toggleAll = () => onSelectionChange?.(allChecked ? [] : selectableKeys);
  const toggleOne = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange?.([...next]);
  };

  /** 같은 열을 다시 누르면 오름 → 내림 → 없음. 「없음」이 있어야 원래 순서로 돌아갈 수 있다. */
  const nextSort = (key: string): SortState => {
    if (sort?.key !== key) return { key, dir: 'asc' };
    if (sort.dir === 'asc') return { key, dir: 'desc' };
    return null;
  };

  const cls = [
    'wc-table',
    stickyHeader ? 'wc-table--sticky-head' : '',
    onRowClick ? 'wc-table--clickable' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  const cellCls = (c: TableColumn<T>, extra = '') =>
    [
      'wc-table__cell',
      c.numeric ? 'wc-table__cell--num' : '',
      c.align && !c.numeric ? `wc-table__cell--${c.align}` : '',
      c.sticky ? 'wc-table__cell--sticky' : '',
      extra,
    ].filter(Boolean).join(' ');

  return (
    <div className={['wc-table-wrap', wrapClassName ?? ''].filter(Boolean).join(' ')}>
      <table className={cls}>
        <caption className="wc-table__caption">{caption}</caption>
        <thead>
          <tr>
            {selectable && (
              <th className="wc-table__cell wc-table__cell--head wc-table__cell--check" scope="col">
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={toggleAll}
                  disabled={selectableKeys.length === 0}
                  aria-label="전체 선택"
                />
              </th>
            )}
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const ariaSort = !c.sortable ? undefined : active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={cellCls(c, 'wc-table__cell--head')}
                  style={c.width ? { width: c.width } : undefined}
                  aria-sort={ariaSort}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="wc-table__sort"
                      onClick={() => onSortChange(nextSort(c.key))}
                    >
                      <span>{c.header}</span>
                      {active
                        ? (sort!.dir === 'asc' ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />)
                        : <ChevronsUpDown className="wc-table__sort-idle" aria-hidden />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
            {rowActions && <th className="wc-table__cell wc-table__cell--actions" scope="col"><span className="wc-table__sr">작업</span></th>}
          </tr>
        </thead>

        <tbody>
          {loading
            ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <tr key={`${uid}-skel-${i}`} className="wc-table__row wc-table__row--skeleton">
                  {Array.from({ length: colCount }, (_, j) => (
                    <td key={j} className="wc-table__cell"><Skeleton height="0.9em" /></td>
                  ))}
                </tr>
              ))
            : rows.length === 0
              ? (
                <tr className="wc-table__row wc-table__row--empty">
                  {/* ★ 표 껍데기는 남긴다 — 머리가 사라지면 「무엇이 없는지」도 사라진다. */}
                  <td className="wc-table__cell wc-table__cell--empty" colSpan={colCount}>{empty}</td>
                </tr>
              )
              : rows.map((row, i) => {
                  const key = rowKey(row, i);
                  const isSel = selected.has(key);
                  return (
                    <tr
                      key={key}
                      className={['wc-table__row', isSel ? 'is-selected' : '', rowClassName?.(row, i) ?? '']
                        .filter(Boolean).join(' ')}
                      onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                      aria-selected={selectable ? isSel : undefined}
                    >
                      {selectable && (
                        <td className="wc-table__cell wc-table__cell--check" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSel}
                            onChange={() => toggleOne(key)}
                            disabled={!(rowSelectable?.(row, i) ?? true)}
                            aria-label={rowSelectionLabel ? rowSelectionLabel(row, i) : `${i + 1}번째 행 선택`}
                          />
                        </td>
                      )}
                      {(() => {
                        /* 칸 합치기 — 앞 칸이 먹은 만큼 뒤 열을 건너뛴다(머리는 그대로다). */
                        let skip = 0;
                        return columns.map((c, ci) => {
                          if (skip > 0) { skip -= 1; return null; }
                          const span = Math.max(1, Math.min(c.colSpan?.(row, i) ?? 1, columns.length - ci));
                          skip = span - 1;
                          const body = c.render ? c.render(row, i) : String((row as Record<string, unknown>)[c.key] ?? '');
                          return c.rowHeader
                            ? (
                              <th key={c.key} scope="row" className={cellCls(c)} colSpan={span > 1 ? span : undefined}>
                                {body}
                              </th>
                            )
                            : (
                              <td key={c.key} className={cellCls(c)} colSpan={span > 1 ? span : undefined}>
                                {body}
                              </td>
                            );
                        });
                      })()}
                      {rowActions && (
                        <td className="wc-table__cell wc-table__cell--actions" onClick={(e) => e.stopPropagation()}>
                          <div className="wc-table__acts">{rowActions(row, i)}</div>
                        </td>
                      )}
                    </tr>
                  );
                })}
        </tbody>

        {footer != null && (
          <tfoot>
            <tr className="wc-table__row wc-table__row--foot">
              <td className="wc-table__cell wc-table__cell--foot" colSpan={colCount}>{footer}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
